'use client'

import { ReactNode, useEffect, useMemo, useRef } from 'react'

import { isResponseSuccessful, Response, ResponseError } from '@marketplace-web/shared/api-client'
import { useLocation } from '@marketplace-web/shared/browser'
import { UiState } from '@marketplace-web/shared/ui-state-data'
import { useDebounce, useLatestCallback } from '@marketplace-web/shared/ui-helpers'
import { getCatalogItems } from 'data/api/get-catalog-items'
import { GetCatalogItemsResp } from 'types/api/response/get-catalog-items-resp'

import { useCatalogContext } from '../CatalogProvider'
import { GeneralReducerAction } from '../CatalogProvider/reducers/actionTypes'
import { CATALOG_FETCH_DEBOUNCE_AMOUNT } from '../constants'
import { incrementCatalogLoad } from '../utils/observability'
import { CATALOG_PER_PAGE } from './constants'
import ItemsCatalogContext, { ItemsCatalogContextType } from './ItemsCatalogContext'
import useItemsCatalogQuery from './useItemsCatalogQuery'
import useItemsTracking from './useItemsTracking'

import usePaginationCommon from './usePaginationCommon'

type Props = {
  children: ReactNode
}

const ItemsCatalogProvider = ({ children }: Props) => {
  const {
    dispatch,
    state: {
      filters,
      dynamicFilters: { selectedDynamicFilters },
      general: { filtersChangeCount },
    },
  } = useCatalogContext()
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const selectedDynamicFiltersRef = useRef(selectedDynamicFilters)
  selectedDynamicFiltersRef.current = selectedDynamicFilters

  const { onItemsSuccess } = useItemsTracking()

  const { time, page, setQuery } = useItemsCatalogQuery()
  const { search_by_image_uuid, search_text } = useLocation().searchParams

  const searchQuery = search_text ? String(search_text) : ''
  const searchQueryRef = useRef(searchQuery)
  searchQueryRef.current = searchQuery

  const { data, error, loadPage, isLoading } = usePaginationCommon(getCatalogItems)

  const indexedData = data[page]

  const uiState = useMemo(() => {
    if (isLoading) return UiState.Pending
    if (error) return UiState.Failure
    if (indexedData) return UiState.Success

    return UiState.Idle
  }, [error, indexedData, isLoading])

  const loadPageDebounced = useDebounce(loadPage, CATALOG_FETCH_DEBOUNCE_AMOUNT)

  const timeRef = useRef(time)
  timeRef.current = time

  const pagination = indexedData?.pagination

  const setTime = useLatestCallback((value: number) => {
    setQuery('time', value)
  })

  const onResponse = useLatestCallback((res: ResponseError | Response<GetCatalogItemsResp>) => {
    if (!isResponseSuccessful(res)) {
      incrementCatalogLoad('error', 'api_items')

      return res
    }

    onItemsSuccess(res)

    try {
      setTime(res.pagination.time)

      dispatch({
        type: GeneralReducerAction.SET_CATALOG_TITLE,
        payload: { title: res.title },
      })

      incrementCatalogLoad('success', 'api_items')
    } catch {
      incrementCatalogLoad('error', 'api_items')
    }

    return res
  })

  useEffect(() => {
    if (filtersChangeCount <= 1) return
    setQuery('page', 1)
  }, [filtersChangeCount, setQuery])

  useEffect(() => {
    if (filtersChangeCount === 0) return

    loadPageDebounced(
      {
        ...filtersRef.current,
        page,
        query: searchQueryRef.current,
        perPage: CATALOG_PER_PAGE,
        selectedDynamicFilters: selectedDynamicFiltersRef.current,
        time: timeRef.current,
        searchByImageUuid: search_by_image_uuid ? String(search_by_image_uuid) : undefined,
      },
      onResponse,
    )
  }, [page, filtersChangeCount, loadPageDebounced, onResponse, search_by_image_uuid])

  const value = useMemo<ItemsCatalogContextType>(
    () => ({
      error,
      items: indexedData?.items,
      uiState,
      page,
      pagination,
    }),
    [error, indexedData?.items, page, pagination, uiState],
  )

  return <ItemsCatalogContext.Provider value={value}>{children}</ItemsCatalogContext.Provider>
}

export default ItemsCatalogProvider
